import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

function Careers() {
    const [scrollY, setScrollY] = useState(0);
    const [windowDimensions, setWindowDimensions] = useState({
        height: typeof window !== 'undefined' ? window.innerHeight : 0,
        width: typeof window !== 'undefined' ? window.innerWidth : 0
    });

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        const handleResize = () => {
            setWindowDimensions({
                height: window.innerHeight,
                width: window.innerWidth
            });
        };

        // Add listeners when component mounts
        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleResize);

        // Remove listeners when component unmounts
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    
    // Only apply the dynamic scroll height calculation on Desktop (lg breakpoint and up)
    const isDesktop = windowDimensions.width >= 1024;
    const dynamicHeight = isDesktop 
        ? Math.min((windowDimensions.height - 550 + scrollY), windowDimensions.height - 86) 
        : 'auto';

    return (
        <div className="pt-[100px] md:pt-[139px] relative overflow-hidden">
            {/* Headline Section */}
            <div id="headline" className="block bg-black flex flex-col justify-center items-center px-6 md:px-16 xl:px-[240px] py-12 md:py-[56px] gap-4 md:gap-[16px] text-center">
                <div className="text-[32px] lg:text-[48px] font-bold leading-tight lg:leading-[50px] z-10 text-white max-w-4xl">
                    <h1>Start Your <span className="font-lora font-medium italic">Career</span> With Us</h1>
                    <p className="text-[16px] font-normal mt-4 lg:mt-0 px-4 md:px-0">
                        We have exciting opportunities for you to gain real-world experience and develop invaluable skills by working alongside our experienced IT professionals.
                    </p>
                </div>
                {/* Hide stars on mobile to prevent overlapping text, show on md and up */}
                <img className="hidden xl:block absolute right-8 lg:right-[56px] animate-float-enhanced-a z-0 w-8 lg:w-auto" src="/icons/star-1.svg" alt="" />
                <img className="hidden xl:block absolute left-8 lg:left-[56px] animate-float-enhanced-b z-0 w-6 lg:w-auto" src="/icons/star-2.svg" alt="" />
            </div>

            {/* Main Content Grid */}
            <div className="flex flex-col lg:grid lg:grid-cols-[35%_auto] px-6 md:px-16 xl:px-[240px] py-12 lg:py-[92px] gap-8 lg:gap-0 relative">
                
                {/* Left Column (Job List) */}
                <div className="lg:px-[32px]">
                    <div className="flex flex-col text-left px-6 lg:px-[32px] py-6 lg:py-[24px] border-2 rounded-[16px] hover:border-black cursor-pointer transition-colors duration-200">
                        <p className="text-[20px] font-semibold">Intern Program</p>
                        <div className="flex flex-row gap-4 lg:gap-[24px] text-[14px] lg:text-[16px] text-grey pt-[4px]">
                            <p>Remote</p>
                            <p>Contract</p>
                        </div>
                        <p className="text-[12px] lg:text-[14px] text-grey pt-[8px]">29 August 2025</p>
                    </div>
                </div>

                {/* Right Column (Job Details) */}
                <div 
                    id="job-detail" 
                    className="self-start w-full lg:max-h-[calc(100vh-86px)] lg:overflow-y-auto border-2 rounded-[16px] lg:sticky lg:top-[86px]"
                >
                    {/* Sticky Header inside Job Details */}
                    <div className="text-left sticky top-0 bg-white py-6 lg:py-[32px] px-6 lg:px-[32px] border-b-2 z-10 rounded-t-[14px]">
                        <h1 className="font-semibold text-2xl lg:text-[32px]">Intern Program</h1>
                        <div className="flex flex-row gap-4 lg:gap-[24px] text-[14px] lg:text-[16px] text-grey pt-[4px]">
                            <p>Remote</p>
                            <p>Contract</p>
                        </div>
                        <p className="text-[12px] lg:text-[14px] text-grey pt-[8px]">29 August 2025</p>
                    </div>

                    {/* Scrollable Description Body */}
                    <div 
                        className="text-left py-6 lg:py-[32px] px-6 lg:px-[32px]" 
                        style={{ height: dynamicHeight }}
                    >
                        <h1 className="font-semibold text-[20px] mb-2">Description</h1>
                        <p className="text-grey leading-relaxed mb-8 text-[14px] lg:text-[16px]">
                            We are looking for driven, adaptable college students ready to step out of the classroom and into active business operations. This program bypasses standard entry-level busywork, offering hands-on experience, cross-functional skill development, and direct involvement in active projects that impact our daily operations.
                        </p>

                        <h2 className="text-[20px] font-semibold pb-[8px] mb-[16px]">What You Will Experience</h2>
                        <ul className="space-y-[12px] lg:space-y-[8px] text-grey mb-8 list-none text-[14px] lg:text-[16px]">
                            <li className="flex items-start">
                                <span className="mr-2 mt-0.5">✔</span>
                                <p><strong className="text-gray-800">Real Project Ownership:</strong> Expect to build, test, analyze, and deploy tangible solutions alongside the core team.</p>
                            </li>
                            <li className="flex items-start">
                                <span className="mr-2 mt-0.5">✔</span>
                                <p><strong className="text-gray-800">Skill Diversification:</strong> Break out of silos by collaborating across departments, learning exactly how engineering, finance, and product quality intersect in a live business environment.</p>
                            </li>
                            <li className="flex items-start">
                                <span className="mr-2 mt-0.5">✔</span>
                                <p><strong className="text-gray-800">Portfolio Building:</strong> Complete your tenure with documented, real-world deliverables that immediately strengthen your resume upon graduation.</p>
                            </li>
                            <li className="flex items-start">
                                <span className="mr-2 mt-0.5">✔</span>
                                <p><strong className="text-gray-800">Professional Mentorship:</strong> Work directly with experienced specialists who will guide your technical execution and professional communication.</p>
                            </li>
                            <li className="flex items-start">
                                <span className="mr-2 mt-0.5">✔</span>
                                <p><strong className="text-gray-800">Professional Mentorship:</strong> Work directly with experienced specialists who will guide your technical execution and professional communication.</p>
                            </li>
                        </ul>

                        <h2 className="text-[20px] font-semibold pb-[8px] mb-[16px]">Open Roles & Focus Areas</h2>
                        {/* Wrapper for responsive horizontal scrolling on mobile */}
                        <div className="overflow-x-auto mb-8 -mx-6 px-6 lg:mx-0 lg:px-0">
                            <table className="w-full text-left border-collapse rounded-lg overflow-hidden shadow-sm border border-gray-200 min-w-[600px] lg:min-w-0">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 lg:px-5 py-3 font-semibold text-[12px] lg:text-[14px] uppercase tracking-wider border-b border-gray-200">Role</th>
                                        <th className="px-4 lg:px-5 py-3 font-semibold text-[12px] lg:text-[14px] uppercase tracking-wider border-b border-gray-200">Primary Responsibilities</th>
                                        <th className="px-4 lg:px-5 py-3 font-semibold text-[12px] lg:text-[14px] uppercase tracking-wider border-b border-gray-200">Target Skills Gained</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 text-grey text-[14px] lg:text-[16px]">
                                    <tr className="hover:bg-gray-50 transition-colors duration-200">
                                        <td className="px-4 lg:px-5 py-4 font-medium text-gray-900 align-top">Web Developer</td>
                                        <td className="px-4 lg:px-5 py-4 align-top">Build and optimize scalable web applications, user interfaces, and backend systems.</td>
                                        <td className="px-4 lg:px-5 py-4 align-top">Full-stack frameworks, API integrations, database management.</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors duration-200 bg-gray-50/50">
                                        <td className="px-4 lg:px-5 py-4 font-medium text-gray-900 align-top">AI Engineer</td>
                                        <td className="px-4 lg:px-5 py-4 align-top">Assist in training models, optimizing data pipelines, and integrating smart solutions.</td>
                                        <td className="px-4 lg:px-5 py-4 align-top">Machine learning workflows, data preprocessing, algorithmic logic.</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors duration-200">
                                        <td className="px-4 lg:px-5 py-4 font-medium text-gray-900 align-top">Quality Assurance</td>
                                        <td className="px-4 lg:px-5 py-4 align-top">Design robust test cases, execute automated testing, and secure product reliability.</td>
                                        <td className="px-4 lg:px-5 py-4 align-top">Automated testing tools, bug lifecycle management, CI/CD exposure.</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors duration-200 bg-gray-50/50">
                                        <td className="px-4 lg:px-5 py-4 font-medium text-gray-900 align-top">Sales Representative</td>
                                        <td className="px-4 lg:px-5 py-4 align-top">Identify and engage prospective clients, conduct product presentations, and drive business growth through successful deal closures.</td>
                                        <td className="px-4 lg:px-5 py-4 align-top">B2B sales strategies, negotiation, CRM software, pipeline management.</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <h2 className="text-[20px] font-semibold border-b-2 border-gray-100 pb-2 mb-4">Program Details & Eligibility</h2>
                        <ul className="list-disc list-outside ml-5 space-y-2 mb-8 text-grey text-[14px] lg:text-[16px]">
                            <li><strong className="text-gray-900">Student Exclusivity:</strong> This program is strictly reserved for active college students. You must be currently enrolled in an accredited university program.</li>
                            <li><strong className="text-gray-900">Flexible Duration:</strong> Engagements are structured in multiple 3-month blocks (e.g., 3, 6, or 9 months). Scale and extend your tenure as your academic schedule allows.</li>
                            <li><strong className="text-gray-900">Work Format:</strong> Remote.</li>
                        </ul>

                        <div className="bg-blue-50 border-l-4 border-blue-600 p-4 lg:p-6 rounded-r-lg">
                            <h3 className="text-lg lg:text-xl font-bold text-blue-900 mb-2">How to Apply</h3>
                            <p className="text-blue-800 leading-relaxed text-[14px] lg:text-[16px]">
                                Send your updated resume and a brief introduction outlining your academic focus to <Link to="/contact-us" className="font-bold underline hover:text-blue-600 transition-colors">contact@harmonimitra.co.id</Link>. Clearly state your preferred role and your available 3-month starting window in the subject line (e.g., <em>"Internship Application: Web Developer - Fall Block"</em>).
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Careers;